// AadharPayReport.jsx (Admin View)

import React, { useEffect, useState, useRef } from 'react';
import { CSVLink } from 'react-csv';
import DataTable from 'react-data-table-component';
import { DatePicker, Tooltip, Select, Modal, Descriptions, Tag, Button, message, Table, Space, Row, Col } from 'antd';
import { toDayDate, queryDate, getExportFormat, getUTCToLocalDateFormat } from './../../../Utils/function';
import { getRequest, putRequest, postRequest } from './../../../Utils/AppApi';
import { FING_PAY_ONBOARDING } from './../../../Utils/appConstants';
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';

// API Endpoints
const GET_AADHAR_PAY_REQUESTS = '/adharPay/all-requests';
const UPDATE_REQUEST_STATUS = '/adharPay/update-status';

// Filter Options
const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_process', label: 'In Process' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
];

function AadharPayReport() {
    const history = useHistory();
    const csvLink = useRef();
    const limit = 10;

    const dateFormat = 'DD-MM-YYYY';

    const [isLoading, setLoading] = useState(false);
    const [searchUsername, setSearchUsername] = useState('');
    const [searchStatus, setSearchStatus] = useState('all');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [getData, setData] = useState([]);
    const [getCsvData, setCsvData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [isLimit, setLimit] = useState(limit);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [actionModalVisible, setActionModalVisible] = useState(false);
    const [actionType, setActionType] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [loadingAction, setLoadingAction] = useState(false);

    // Get FP Status display text
    const getFPStatusText = (status, isCompleted) => {
        if (isCompleted) return 'Active';
        if (status === true) return 'Onboarded';
        if (status === false) return 'Pending';
        return 'Not Available';
    };

    const getFPStatusColor = (status, isCompleted) => {
        if (isCompleted) return 'green';
        if (status === true) return 'blue';
        if (status === false) return 'orange';
        return 'default';
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    // Bank table columns
    const bankColumns = [
        { title: 'Account Number', dataIndex: 'accountNumber', key: 'accountNumber', width: '180px' },
        { title: 'IFSC Code', dataIndex: 'ifscCode', key: 'ifscCode', width: '120px' },
        { title: 'Bank Name', dataIndex: 'bankName', key: 'bankName', width: '150px' },
        { title: 'Account Holder', dataIndex: 'name', key: 'name', width: '150px' },
        {
            title: 'Default', dataIndex: 'isDefault', key: 'isDefault', width: '80px',
            render: (isDefault) => <Tag color={isDefault ? 'green' : 'default'}>{isDefault ? 'Yes' : 'No'}</Tag>
        },
        {
            title: 'Status', dataIndex: 'isAdminApproved', key: 'isAdminApproved', width: '100px',
            render: (status) => {
                if (status === 1) return <Tag color="green">Approved</Tag>;
                if (status === 2) return <Tag color="red">Rejected</Tag>;
                return <Tag color="gold">Pending</Tag>;
            }
        }
    ];

    // Table Columns
    const columns = [
        { name: 'S.No', selector: (row, index) => ((page - 1) * isLimit) + index + 1, width: "60px", center: true },
        { name: 'Request Date & Time', cell: row => formatDateTime(row.createdAt), width: "160px", sortable: true },
        { name: 'Request ID', selector: row => row.requestId || '', width: "250px" },
        { name: 'Name', selector: row => row.name || row.userDetails?.name || row.username, width: "150px", sortable: true },
        { name: 'Username', selector: row => row.username, width: "130px", sortable: true },
        { name: 'Mobile', selector: row => row.mobile, width: "120px" },
        { name: 'PAN Number', selector: row => row.pancard || '-', width: "130px" },
        { name: 'Aadhar Number', selector: row => row.aadharNumber || '-', width: "150px" },
        {
            name: 'FP ID', cell: row => (
                <Tooltip title={row.fingPayMerchantLoginId || row.fingPayMerchantId || '-'}>
                    <span style={{ fontSize: '12px' }}>{row.fingPayMerchantLoginId || row.fingPayMerchantId || '-'}</span>
                </Tooltip>
            ), width: "140px"
        },
        {
            name: 'FP Status', cell: row => (
                <Tag color={getFPStatusColor(row.fingPayStatus, row.fingPayIsCompleted)}>
                    {getFPStatusText(row.fingPayStatus, row.fingPayIsCompleted)}
                </Tag>
            ), width: "110px"
        },
        {
            name: 'Request Status', cell: row => (
                <Tag color={row.status === 'pending' ? 'gold' : row.status === 'in_process' ? 'blue' : row.status === 'approved' ? 'green' : 'red'}>
                    {row.status === 'in_process' ? 'IN PROCESS' : row.status.toUpperCase()}
                </Tag>
            ), width: "120px"
        },
        {
            name: 'Actions',
            cell: row => (
                <Button
                    type="link"
                    size="small"
                    onClick={() => viewRequestDetails(row)}
                    style={{ padding: '4px 8px', fontWeight: 500 }}
                >
                    View Details
                </Button>
            ),
            width: "120px"
        }
    ];

    const objectToQueryString = (obj) => {
        var str = [];
        for (var p in obj)
            if (obj.hasOwnProperty(p) && obj[p] !== null && obj[p] !== '' && obj[p] !== 'all') {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        return str.join("&");
    }

    const handleReOnboard = (data) => {
        let payload = { username: data.userData?.username || data.username }
        postRequest(`${FING_PAY_ONBOARDING}`, payload, history).then((response) => {
            if (response.success === true) {
                toast.success(response.response?.remarks || "Re-onboard request sent successfully");
                getDataReports();
            } else {
                toast.error(response.data || "Failed to send re-onboard request");
            }
        }).catch(function (error) {
            toast.error("Something went wrong");
            console.log(error);
        })
    }

    // In AadharPayReport.jsx - Fix date handling

    const getDataReports = (newLimit) => {

        setLoading(true);
        let params = { page: page, limit: isLimit };

        if (newLimit) {
            setLimit(newLimit);
            params.limit = newLimit;
            params.page = 1;
            setPage(1);
        }

        // Fix date handling - send dates in YYYY-MM-DD format
        if (startDate) {
            const startDateObj = new Date(startDate);
            params.startDate = startDateObj.toISOString().split('T')[0];
        }

        if (endDate) {
            const endDateObj = new Date(endDate);
            params.endDate = endDateObj.toISOString().split('T')[0];
        }

        if (searchUsername) params.searchUserId = searchUsername;
        if (searchStatus !== 'all') params.status = searchStatus;

        const queryString = objectToQueryString(params);

        getRequest(`${GET_AADHAR_PAY_REQUESTS}?${queryString}`, history).then((response) => {
            if (response.success === true) {
                setData(response.data.docs || []);
                setTotalRecord(response.data.totalDocs || 0);
                setPage(response.data.page || 1);
            } else {
                setData([]);
                setTotalRecord(0);
                setPage(1);
            }
            setLoading(false);
        }).catch(function (error) {
            console.log(error);
            setLoading(false);
            message.error("Failed to fetch data");
        });
        
    };

    const getCSVDataList = () => {
        let params = { exportsCsv: true };
        if (startDate) params.startDate = startDate ? queryDate(startDate) : "";
        if (endDate) params.endDate = endDate ? queryDate(endDate) : "";
        if (searchUsername) params.searchUserId = searchUsername;
        if (searchStatus !== 'all') params.status = searchStatus;

        const queryString = objectToQueryString(params);

        getRequest(`${GET_AADHAR_PAY_REQUESTS}?${queryString}`, history).then((response) => {
            if (response.success === true) {
                let csvdataRecord = [];
                let csvdata = response.data.docs || [];
                csvdata.map((e) => {
                    return csvdataRecord.push({
                        "Request ID": e.requestId,
                        "Request Date": getExportFormat(e.createdAt),
                        "Request Time": new Date(e.createdAt).toLocaleTimeString('en-IN'),
                        "Name": e.name || e.userDetails?.name || e.username,
                        "Username": e.username,
                        "Mobile": e.mobile,
                        "PAN Number": e.pancard || '-',
                        "Aadhar Number": e.aadharNumber || '-',
                        "FP ID": e.fingPayMerchantLoginId || e.fingPayMerchantId || '-',
                        "FP Status": getFPStatusText(e.fingPayStatus, e.fingPayIsCompleted),
                        "Request Status": e.status,
                        "Created By": e.createdByDetails?.username || '-',
                        "In Process By": e.inProcessByDetails?.username || '-',
                        "Approved By": e.approvedByDetails?.username || '-',
                        "Rejected By": e.rejectedByDetails?.username || '-',
                        "Rejection Reason": e.rejectionReason || '-'
                    });
                });
                setCsvData(csvdataRecord);
                setTimeout(() => { csvLink.current.link.click(); }, 1000);
                message.success("CSV exported successfully");
            } else {
                message.error("No data to export");
                setCsvData([]);
            }
        }).catch(function (error) {
            console.log(error);
            message.error("Failed to export CSV");
        });
    }

    const handlePageChange = (page) => setPage(page);
    const handleRowsPerPageChange = (newLimit) => { setLimit(newLimit); setPage(1); getDataReports(newLimit); };
    const onStartDateChange = (date, dateString) => setStartDate(date);
    const onEndDateChange = (date, dateString) => setEndDate(date);
    const handleSearch = () => { setPage(1); getDataReports(); };

    const handleClear = () => {
        setStartDate(null);
        setEndDate(null);
        setSearchUsername('');
        setSearchStatus('all');
        setTimeout(() => {
            setPage(1);
            getDataReports();
        }, 100);
    };

    const viewRequestDetails = (row) => {
        setSelectedRequest(row);
        setModalVisible(true);
    };

    const showActionModal = (row, action) => {
        setSelectedRequest(row);
        setActionType(action);
        setRejectReason('');
        setActionModalVisible(true);
    };

    const updateRequestStatus = async () => {
        if (!selectedRequest) return;
        if (actionType === 'rejected' && !rejectReason) {
            message.error('Please provide a rejection reason');
            return;
        }

        setLoadingAction(true);
        try {
            const payload = {
                requestId: selectedRequest.requestId,
                status: actionType
            };
            if (actionType === 'rejected') payload.rejectionReason = rejectReason;

            const response = await putRequest(UPDATE_REQUEST_STATUS, payload, history);
            if (response.success) {
                const statusText = actionType === 'in_process' ? 'marked as In Process' : actionType;
                message.success(`Request ${statusText} successfully`);
                setActionModalVisible(false);
                setModalVisible(false);
                getDataReports();
            } else {
                message.error(response.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            message.error('Failed to update status');
        } finally {
            setLoadingAction(false);
        }
    };

    useEffect(() => { getDataReports(); }, [page]);

    // Get action buttons based on status (for modal) - IMPROVED STYLING
    const getActionButtons = (row) => {
        const actionButtons = [];

        // Re-onboard button - ALWAYS SHOW
        actionButtons.push(
            <Button
                key="re-onboard"
                type="primary"
                size="middle"
                style={{
                    backgroundColor: '#ff5918',
                    borderColor: '#ff5918',
                    borderRadius: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 500,
                    padding: '8px 20px'
                }}
                onClick={() => handleReOnboard(row)}
            >
                <i className="fa fa-refresh" style={{ fontSize: '14px' }}></i>
                Re-Onboard
            </Button>
        );

        // Status-specific buttons
        if (row.status === 'pending' || row.status === 'in_process') {
            if (row.status === 'pending') {
                actionButtons.push(
                    <Button
                        key="in-process"
                        type="primary"
                        size="middle"
                        style={{
                            backgroundColor: '#1890ff',
                            borderColor: '#1890ff',
                            borderRadius: '8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontWeight: 500,
                            padding: '8px 20px'
                        }}
                        onClick={() => showActionModal(row, 'in_process')}
                    >
                        <i className="fa fa-clock-o" style={{ fontSize: '14px' }}></i>
                        In Process
                    </Button>
                );
            }
            actionButtons.push(
                <Button
                    key="approve"
                    type="primary"
                    size="middle"
                    style={{
                        backgroundColor: '#52c41a',
                        borderColor: '#52c41a',
                        borderRadius: '8px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontWeight: 500,
                        padding: '8px 20px'
                    }}
                    onClick={() => showActionModal(row, 'approved')}
                >
                    <i className="fa fa-check-circle" style={{ fontSize: '14px' }}></i>
                    Approve
                </Button>,
                <Button
                    key="reject"
                    danger
                    size="middle"
                    style={{
                        borderRadius: '8px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontWeight: 500,
                        padding: '8px 20px'
                    }}
                    onClick={() => showActionModal(row, 'rejected')}
                >
                    <i className="fa fa-ban" style={{ fontSize: '14px' }}></i>
                    Reject
                </Button>
            );
        }

        return (
            <Space size="middle" wrap style={{ justifyContent: 'center', width: '100%', gap: '12px' }}>
                {actionButtons}
            </Space>
        );
    };

    return (
        <div>
            <div className="content-wrapper pt-2">
                <div className="content-header my-0 py-0">
                    <div className="container-fluid">
                        <Row gutter={16} align="middle">
                            <Col xs={24} sm={12} md={8}>
                                <h1 className='page-title text-blue font-merriweather' style={{ marginBottom: 0 }}>
                                    Aadhar Pay <span className='text-red'> Requests Report</span>
                                </h1>
                            </Col>
                        </Row>
                    </div>
                </div>

                <div className="content">
                    <div className="container-fluid pt-2 pb-5">
                        <div className='card col-md-12'>
                            <div className='card-body'>
                                {/* Filter Row - Properly Aligned */}
                                <Row gutter={[16, 16]} align="bottom">
                                    <Col xs={24} sm={12} md={6} lg={4}>
                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className='mb-1 text-blue'>From Date</label>
                                            <DatePicker
                                                className="form-control form-control-border w-100"
                                                format={dateFormat}
                                                value={startDate}
                                                onChange={onStartDateChange}
                                                placeholder="Start Date"
                                                style={{ width: '100%', borderRadius: '8px' }}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6} lg={4}>
                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className='mb-1 text-blue'>To Date</label>
                                            <DatePicker
                                                className="form-control form-control-border w-100"
                                                format={dateFormat}
                                                value={endDate}
                                                onChange={onEndDateChange}
                                                placeholder="End Date"
                                                style={{ width: '100%', borderRadius: '8px' }}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={5}>
                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className='mb-1 text-blue'>Search (Name/Username/Mobile)</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-border"
                                                placeholder="Search by name, username or mobile"
                                                onChange={(e) => setSearchUsername(e.target.value)}
                                                value={searchUsername}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                                style={{ width: '100%', borderRadius: '8px' }}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6} lg={4}>
                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className='mb-1 text-blue'>Status</label>
                                            <Select
                                                options={statusOptions}
                                                className="w-100"
                                                value={searchStatus}
                                                onChange={(e) => setSearchStatus(e)}
                                                placeholder="Select status"
                                                style={{ width: '100%', borderRadius: '8px' }}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6} md={4} lg={3}>
                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className='mb-1 text-blue'>&nbsp;</label>
                                            <div>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary rounded-pill px-3 w-100"
                                                    onClick={handleSearch}
                                                    style={{ whiteSpace: 'nowrap', borderRadius: '8px' }}
                                                >
                                                    <i className="fa fa-search" style={{ marginRight: '8px' }}></i> Search
                                                </button>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6} md={4} lg={3}>
                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className='mb-1 text-blue'>&nbsp;</label>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-default rounded-pill px-4 w-100"
                                                    onClick={handleClear}
                                                    style={{ whiteSpace: 'nowrap', borderRadius: '8px' }}
                                                >
                                                    <i className="fa fa-refresh" style={{ marginRight: '8px' }}></i> Clear
                                                </button>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Export Button Row */}
                                <Row className="mt-3">
                                    <Col xs={24}>
                                        <div className='float-sm-right'>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={getCSVDataList}
                                                style={{ borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                            >
                                                <i className="fa fa-file-excel"></i> Export to CSV
                                            </button>
                                            <CSVLink
                                                data={getCsvData}
                                                filename={`aadhar-pay-requests-${queryDate(startDate) || 'all'}-to-${queryDate(endDate) || 'all'}`}
                                                className='hidden'
                                                ref={csvLink}
                                                target='_blank'
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                {/* Data Table */}
                                <Row className="mt-3">
                                    <Col xs={24}>
                                        <DataTable
                                            columns={columns}
                                            data={getData}
                                            highlightOnHover
                                            pagination
                                            paginationServer
                                            progressPending={isLoading}
                                            paginationTotalRows={getTotalRecord}
                                            onChangeRowsPerPage={handleRowsPerPageChange}
                                            onChangePage={handlePageChange}
                                            paginationRowsPerPageOptions={[5, 10, 20, 50, 100]}
                                            noDataComponent="No Aadhar Pay requests found"
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Request Details Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="fa fa-info-circle text-blue" style={{ fontSize: '20px' }}></i>
                        <span style={{ fontSize: '18px', fontWeight: '600' }}>Request Details</span>
                    </div>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setModalVisible(false)}>Close</Button>
                ]}
                width={1000}
                centered
            >
                {selectedRequest && (
                    <>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Request ID" span={2}>
                                <strong style={{ color: '#1890ff' }}>{selectedRequest.requestId}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Request Date & Time">
                                {formatDateTime(selectedRequest.createdAt)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={selectedRequest.status === 'pending' ? 'gold' : selectedRequest.status === 'in_process' ? 'blue' : selectedRequest.status === 'approved' ? 'green' : 'red'}>
                                    {selectedRequest.status === 'in_process' ? 'IN PROCESS' : selectedRequest.status.toUpperCase()}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Name">
                                {selectedRequest.name || selectedRequest.userDetails?.name || selectedRequest.username}
                            </Descriptions.Item>
                            <Descriptions.Item label="Username">
                                {selectedRequest.username}
                            </Descriptions.Item>
                            <Descriptions.Item label="Mobile">
                                {selectedRequest.mobile}
                            </Descriptions.Item>
                            <Descriptions.Item label="Email">
                                {selectedRequest.email || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="PAN Number">
                                <strong>{selectedRequest.pancard || '-'}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Aadhar Number">
                                <strong>{selectedRequest.aadharNumber || '-'}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Outlet Name">
                                {selectedRequest.outletName || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="FP ID">
                                <Tag color="purple">{selectedRequest.fingPayMerchantLoginId || selectedRequest.fingPayMerchantId || '-'}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="FP Status">
                                <Tag color={getFPStatusColor(selectedRequest.fingPayStatus, selectedRequest.fingPayIsCompleted)}>
                                    {getFPStatusText(selectedRequest.fingPayStatus, selectedRequest.fingPayIsCompleted)}
                                </Tag>
                            </Descriptions.Item>
                            {selectedRequest.inProcessAt && (
                                <Descriptions.Item label="In Process At">
                                    {formatDateTime(selectedRequest.inProcessAt)}
                                </Descriptions.Item>
                            )}
                            {selectedRequest.inProcessByDetails && (
                                <Descriptions.Item label="In Process By">
                                    {selectedRequest.inProcessByDetails.username}
                                </Descriptions.Item>
                            )}
                            {selectedRequest.approvedAt && (
                                <Descriptions.Item label="Approved At">
                                    {formatDateTime(selectedRequest.approvedAt)}
                                </Descriptions.Item>
                            )}
                            {selectedRequest.approvedByDetails && (
                                <Descriptions.Item label="Approved By">
                                    {selectedRequest.approvedByDetails.username}
                                </Descriptions.Item>
                            )}
                            {selectedRequest.rejectedAt && (
                                <Descriptions.Item label="Rejected At">
                                    {formatDateTime(selectedRequest.rejectedAt)}
                                </Descriptions.Item>
                            )}
                            {selectedRequest.rejectedByDetails && (
                                <Descriptions.Item label="Rejected By">
                                    {selectedRequest.rejectedByDetails.username}
                                </Descriptions.Item>
                            )}
                            {selectedRequest.rejectionReason && (
                                <Descriptions.Item label="Rejection Reason" span={2}>
                                    <span className="text-danger">{selectedRequest.rejectionReason}</span>
                                </Descriptions.Item>
                            )}
                        </Descriptions>

                        {/* Bank Details Section */}
                        {selectedRequest.bankDetails && selectedRequest.bankDetails.length > 0 && (
                            <div className="mt-4">
                                <h4 className="text-blue mb-3" style={{ borderLeft: '4px solid #1890ff', paddingLeft: '12px' }}>
                                    <i className="fa fa-university"></i> Bank Account Details
                                </h4>
                                <Table
                                    columns={bankColumns}
                                    dataSource={selectedRequest.bankDetails}
                                    rowKey="_id"
                                    pagination={false}
                                    size="small"
                                    bordered
                                />
                            </div>
                        )}

                        {/* Action Buttons Section */}
                        <div className="mt-4" style={{ borderTop: '1px solid #e8e8e8', paddingTop: '24px', marginTop: '24px' }}>
                            <h4 className="text-blue mb-3" style={{ textAlign: 'center', fontSize: '16px', fontWeight: 600 }}>
                                <i className="fa fa-tasks"></i> Actions
                            </h4>
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
                                {getActionButtons(selectedRequest)}
                            </div>
                        </div>
                    </>
                )}
            </Modal>

            {/* Action Confirmation Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className={`fa ${actionType === 'approved' ? 'fa-check-circle text-success' : actionType === 'in_process' ? 'fa-clock-o text-blue' : 'fa-ban text-danger'}`} style={{ fontSize: '20px' }}></i>
                        <span>{actionType === 'approved' ? 'Approve Request' : actionType === 'in_process' ? 'Mark In Process' : 'Reject Request'}</span>
                    </div>
                }
                open={actionModalVisible}
                onCancel={() => setActionModalVisible(false)}
                onOk={updateRequestStatus}
                okText={actionType === 'approved' ? 'Approve' : actionType === 'in_process' ? 'Mark In Process' : 'Reject'}
                okButtonProps={{ danger: actionType === 'rejected', loading: loadingAction }}
                cancelButtonProps={{ disabled: loadingAction }}
                width={500}
                centered
            >
                <p>Are you sure you want to <strong>{actionType === 'approved' ? 'approve' : actionType === 'in_process' ? 'mark as in process' : 'reject'}</strong> this request?</p>
                <hr />
                <p><strong>Request ID:</strong> {selectedRequest?.requestId}</p>
                <p><strong>Name:</strong> {selectedRequest?.name || selectedRequest?.username}</p>
                <p><strong>Username:</strong> {selectedRequest?.username}</p>
                <p><strong>Mobile:</strong> {selectedRequest?.mobile}</p>
                {actionType === 'rejected' && (
                    <div className="form-group mt-3">
                        <label className="text-danger"><strong>Rejection Reason:</strong> <span className="text-danger">*</span></label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Please provide a reason for rejection"
                            disabled={loadingAction}
                            style={{ borderRadius: '8px' }}
                        />
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default AadharPayReport;